import React, { useRef, useState } from 'react';
import { Badge, Button, Alert, Spinner, Form, Row, Col } from 'react-bootstrap';
import { vehicleAPI } from '../../services/api';
import {
  FiUploadCloud, FiFileText, FiFolder, FiCheckCircle,
  FiXCircle, FiAlertTriangle, FiInfo, FiRefreshCw, FiArchive,
} from 'react-icons/fi';

interface ImportResult {
  plateNumber: string;
  status: 'success' | 'error';
  isNew?: boolean;
  insuranceLinked?: boolean;
  insuranceMissing?: boolean;
  licenseLinked?: boolean;
  licenseMissing?: boolean;
  message?: string;
}

interface SkippedResult {
  plateNumber: string;
  reason: string;
  insurancePath?: string;
  licensePath?: string;
}

const BulkImportPage: React.FC = () => {
  const [excelFiles, setExcelFiles] = useState<File[]>([]);
  const [pdfFiles, setPdfFiles]     = useState<File[]>([]);
  const [zipFiles, setZipFiles]     = useState<File[]>([]);
  const [importLimit, setImportLimit] = useState<string>('');
  const [serverDir, setServerDir]   = useState<string>('');
  const [importing, setImporting]   = useState(false);
  const [results, setResults]       = useState<ImportResult[] | null>(null);
  const [skipped, setSkipped]       = useState<SkippedResult[]>([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [error, setError]           = useState<string | null>(null);

  const excelRef = useRef<HTMLInputElement>(null);
  const pdfRef   = useRef<HTMLInputElement>(null);
  const zipRef   = useRef<HTMLInputElement>(null);

  const handleImport = async () => {
    if (excelFiles.length === 0 && pdfFiles.length === 0 && zipFiles.length === 0 && !serverDir.trim()) {
      setError('Please select an Excel file, PDF/ZIP files, or enter a server directory path.');
      return;
    }
    setImporting(true);
    setError(null);
    setResults(null);
    try {
      const limit = importLimit ? parseInt(importLimit) : undefined;
      const response = await vehicleAPI.bulkImport(excelFiles, pdfFiles, zipFiles, limit, serverDir || undefined);
      setResults(response.data.results);
      setSkipped(response.data.skipped ?? []);
      setSkippedCount(response.data.skippedCount ?? 0);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data || err.message || 'Import failed.';
      const detail = err.response?.data?.detail ? `\n${err.response.data.detail}` : '';
      setError(String(msg) + String(detail));
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setExcelFiles([]);
    setPdfFiles([]);
    setZipFiles([]);
    setImportLimit('');
    setServerDir('');
    setResults(null);
    setSkipped([]);
    setSkippedCount(0);
    setError(null);
    if (excelRef.current) excelRef.current.value = '';
    if (pdfRef.current)   pdfRef.current.value   = '';
    if (zipRef.current)   zipRef.current.value    = '';
  };

  const successCount = results?.filter(r => r.status === 'success').length ?? 0;
  const errorCount   = results?.filter(r => r.status === 'error').length   ?? 0;
  const newCount     = results?.filter(r => r.isNew).length                ?? 0;

  return (
    <>
      <div className="page-header">
        <h2>Bulk Import</h2>
      </div>

      {/* How it works */}
      <div className="section-card mb-4">
        <div className="section-card-header">
          <span className="section-card-title"><FiInfo style={{ color: '#0891b2' }} /> How it works</span>
        </div>
        <div className="section-card-body">
          <ol style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', lineHeight: 2, color: '#374151' }}>
            <li>Prepare an <strong>Excel file (.xlsx)</strong> with columns: <code>Arabic Plate</code>, <code>Plate Number</code>, <code>Insurance Filename</code>, <code>License Filename</code></li>
            <li>Select the <strong>folder</strong> containing all PDFs — subfolders are scanned automatically</li>
            <li>Click <strong>Import</strong> — filenames in Excel are matched against uploaded PDFs (folder paths ignored)</li>
            <li>Rows where <strong>any PDF is missing</strong> are skipped and reported separately</li>
          </ol>
        </div>
      </div>

      {/* Upload */}
      <div className="section-card mb-4">
        <div className="section-card-header">
          <span className="section-card-title"><FiUploadCloud style={{ color: '#16a34a' }} /> Upload Files</span>
        </div>
        <div className="section-card-body">
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

          <Row className="g-3 mb-4">
            <Col md={6}>
              <div style={{
                border: '2px dashed #e2e8f0', borderRadius: 12, padding: '1.5rem',
                background: '#fafbfc', textAlign: 'center',
                transition: 'border-color 0.2s',
              }}>
                <FiFileText size={28} style={{ color: '#94a3b8', marginBottom: '0.5rem', display: 'block', margin: '0 auto 0.5rem' }} />
                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>Excel Files</div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.75rem' }}>Select one or more .xlsx files</div>
                <Form.Control
                  ref={excelRef}
                  type="file"
                  accept=".xlsx,.xls"
                  multiple
                  onChange={e => setExcelFiles(Array.from((e.target as HTMLInputElement).files ?? []))}
                  disabled={importing}
                  style={{ fontSize: '0.82rem' }}
                />
                {excelFiles.length > 0 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#059669', fontWeight: 500 }}>
                    <FiCheckCircle className="me-1" />
                    {excelFiles.length} file{excelFiles.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div style={{
                border: '2px dashed #e2e8f0', borderRadius: 12, padding: '1.5rem',
                background: '#fafbfc', textAlign: 'center',
              }}>
                <FiFolder size={28} style={{ color: '#94a3b8', display: 'block', margin: '0 auto 0.5rem' }} />
                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>PDF Folder <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.75rem' }}>Skip if PDFs are already uploaded to the server</div>
                <Form.Control
                  ref={pdfRef}
                  type="file"
                  // @ts-ignore
                  webkitdirectory=""
                  onChange={e => {
                    const all = Array.from((e.target as HTMLInputElement).files ?? []);
                    setPdfFiles(all.filter(f => f.name.toLowerCase().endsWith('.pdf')));
                  }}
                  disabled={importing}
                  style={{ fontSize: '0.82rem' }}
                />
                {pdfFiles.length > 0 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#059669', fontWeight: 500 }}>
                    <FiCheckCircle className="me-1" />
                    {pdfFiles.length} PDF{pdfFiles.length !== 1 ? 's' : ''} found
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={6}>
              <div style={{
                border: '2px dashed #e2e8f0', borderRadius: 12, padding: '1.5rem',
                background: '#fafbfc', textAlign: 'center',
              }}>
                <FiArchive size={28} style={{ color: '#94a3b8', display: 'block', margin: '0 auto 0.5rem' }} />
                <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.25rem' }}>ZIP File <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional)</span></div>
                <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: '0.75rem' }}>Upload a .zip containing all PDFs — no FTP needed</div>
                <Form.Control
                  ref={zipRef}
                  type="file"
                  accept=".zip"
                  multiple
                  onChange={e => setZipFiles(Array.from((e.target as HTMLInputElement).files ?? []))}
                  disabled={importing}
                  style={{ fontSize: '0.82rem' }}
                />
                {zipFiles.length > 0 && (
                  <div style={{ marginTop: '0.5rem', fontSize: '0.78rem', color: '#059669', fontWeight: 500 }}>
                    <FiCheckCircle className="me-1" />
                    {zipFiles.length} ZIP{zipFiles.length !== 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <div className="mb-3">
            <Form.Group>
              <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                Server PDF directory <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional — path on server where PDFs/ZIPs are already stored)</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="e.g. C:\inetpub\wwwroot\nfc-api\pdf-uploads"
                value={serverDir}
                onChange={e => setServerDir(e.target.value)}
                disabled={importing}
                style={{ fontSize: '0.82rem', fontFamily: 'monospace' }}
              />
              <Form.Text style={{ color: '#64748b', fontSize: '0.78rem' }}>
                If PDFs are stored somewhere other than the standard upload folders, enter the server path here. Subfolders and ZIPs are scanned automatically.
              </Form.Text>
            </Form.Group>
          </div>

          <div className="d-flex align-items-center gap-3 mb-3">
            <Form.Group style={{ maxWidth: 220 }}>
              <Form.Label style={{ fontSize: '0.82rem', fontWeight: 600 }}>
                Import limit <span style={{ fontWeight: 400, color: '#94a3b8' }}>(optional — leave blank for all)</span>
              </Form.Label>
              <Form.Control
                type="number"
                min={1}
                placeholder="e.g. 500"
                value={importLimit}
                onChange={e => setImportLimit(e.target.value)}
                disabled={importing}
                style={{ fontSize: '0.82rem' }}
              />
            </Form.Group>
          </div>

          <div className="d-flex gap-2">
            <Button
              variant="primary"
              onClick={handleImport}
              disabled={importing || (excelFiles.length === 0 && pdfFiles.length === 0 && zipFiles.length === 0 && !serverDir.trim())}
              className="d-flex align-items-center gap-2"
            >
              {importing
                ? <><Spinner size="sm" animation="border" />Importing…</>
                : <><FiUploadCloud /> Import</>}
            </Button>
            {(results || excelFiles.length > 0 || pdfFiles.length > 0 || serverDir.trim()) && (
              <Button variant="outline-secondary" onClick={handleReset} disabled={importing}
                className="d-flex align-items-center gap-2">
                <FiRefreshCw /> Reset
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div className="section-card">
          <div className="section-card-header" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
            <span className="section-card-title">Import Results</span>
            <div className="d-flex gap-2 flex-wrap">
              <Badge bg="success">{successCount} processed</Badge>
              {newCount      > 0 && <Badge bg="info">{newCount} new</Badge>}
              {skippedCount  > 0 && <Badge bg="warning" text="dark">{skippedCount} skipped</Badge>}
              {errorCount    > 0 && <Badge bg="danger">{errorCount} errors</Badge>}
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="table mb-0">
              <thead>
                <tr>
                  <th>Plate Number</th>
                  <th>Status</th>
                  <th>Vehicle</th>
                  <th>License PDF</th>
                  <th>Insurance PDF</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i}>
                    <td><strong style={{ fontFamily: 'monospace' }}>{r.plateNumber}</strong></td>
                    <td>
                      {r.status === 'success'
                        ? <Badge bg="success" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                            <FiCheckCircle /> Success
                          </Badge>
                        : <Badge bg="danger" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                            <FiXCircle /> Error
                          </Badge>}
                    </td>
                    <td>
                      {r.status === 'success' && (r.isNew
                        ? <Badge bg="info">Created</Badge>
                        : <Badge bg="secondary">Updated</Badge>)}
                    </td>
                    <td>
                      {r.status === 'success' && (
                        r.licenseLinked
                          ? <Badge bg="success">Linked</Badge>
                          : r.licenseMissing
                            ? <Badge bg="warning" text="dark">Not found</Badge>
                            : <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {r.status === 'success' && (
                        r.insuranceLinked
                          ? <Badge bg="success">Linked</Badge>
                          : r.insuranceMissing
                            ? <Badge bg="warning" text="dark">Not found</Badge>
                            : <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {r.status === 'error' && (
                        <span style={{ color: '#dc2626', fontSize: '0.8rem' }}>{r.message}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Skipped rows */}
          {skipped.length > 0 && (
            <>
              <div style={{
                padding: '0.875rem 1.25rem',
                background: '#fffbeb',
                borderTop: '1px solid #fde68a',
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                fontSize: '0.875rem', fontWeight: 600, color: '#92400e',
              }}>
                <FiAlertTriangle />
                Skipped Rows ({skipped.length})
                <span style={{ fontWeight: 400, color: '#b45309' }}>— one or both PDFs missing</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-sm mb-0">
                  <thead>
                    <tr>
                      <th>Plate Number</th>
                      <th>Reason</th>
                      <th>Expected Insurance</th>
                      <th>Expected License</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skipped.map((s, i) => (
                      <tr key={i} style={{ background: '#fffbeb' }}>
                        <td><strong>{s.plateNumber}</strong></td>
                        <td style={{ color: '#64748b', fontSize: '0.8rem' }}>{s.reason}</td>
                        <td style={{ color: '#64748b', fontSize: '0.8rem' }}>{s.insurancePath || '—'}</td>
                        <td style={{ color: '#64748b', fontSize: '0.8rem' }}>{s.licensePath   || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default BulkImportPage;
